import * as React from 'react';
import { defaultMemoize } from 'reselect';
import { cssClass } from '~src/style';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { Subspace, TabbedSpace } from '../../app/spaces/TabbedSpace';
import { ISpaceInfoTypeItemCount } from '../../app/state/ContentCountsState';
import { ISanitizer } from '../../commonInfra/utils/Sanitize';
import { LensSubspaceName, SpaceInfo } from '../../app/state/SpaceInfo';
import { Messages } from '../../codegen/Localize';
import { ILensDetailInfoWithUsage } from '../../ts/models/ILensInfo';
import { ILensActions } from '../../ts/ServerApi/types/ILensAction';
import { LensSubspaceProps } from '../types/LensSubspaceProps';
import { LensSpaceHeader } from './LensSpaceHeader';
import { NaturalLanguageDatasourceOwnerPanel } from '@tableau/natural-language-datasource-owner-panel';
import { SessionSiteSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { LensUsageAnalytics } from '../usageAnalytics/LensUsageAnalytics';

export interface LensSpaceStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly urls: SpacesSiteUrls;
  readonly catalogingOnSiteEnabled: boolean;
  readonly contentCounts: ISpaceInfoTypeItemCount<'lens'>;
  readonly contentCountsUpToDate: boolean;
  readonly siteUrlName: string;
}

export interface LensSpaceDispatchProps {
  readonly fetchUpdatedContentCounts: () => void;
}

export interface LensSpaceOwnProps {
  readonly lensDetailInfoWithUsage: ILensDetailInfoWithUsage;
  readonly allowedActions: ILensActions;
  readonly subspaceProps: LensSubspaceProps;
  readonly canEdit: boolean;
  readonly reloadSpace: () => void;
  readonly sanitizer: ISanitizer;
}

export type LensSpaceProps = LensSpaceStateProps & LensSpaceOwnProps & LensSpaceDispatchProps;

function askDataSubspace(
  subspaceProps: LensSubspaceProps,
  siteUrlName: string,
): Subspace {
  return {
    name: 'askData',
    tabLabel: Messages.nlpTab(),
    renderContent: () => <iframe
      title='Ask data Lens interface'
      src={getNlpWorkbookUrl(
        siteUrlName,
        subspaceProps.lensDetailInfo.repositoryUrl)}
      className={iframeStyle}
    />,
    shouldRenderTabContentCount: false
  };
}


function getSubspaces(
  subspaceProps:  LensSubspaceProps,
  siteUrlName: string,
): ReadonlyArray<Subspace> {
  return [
    askDataSubspace(subspaceProps, siteUrlName),
  ];
}

export class LensSpace extends React.PureComponent<LensSpaceProps> {
  private memoizedGetSubspaces = defaultMemoize(getSubspaces);

  public urlForSubspace = (subspaceName?: LensSubspaceName): string => {
    switch (subspaceName) {
      case 'askData':
        return this.props.urls.lensUrl(this.props.lensDetailInfoWithUsage.id);
      case 'usageAnalytics':
        return this.props.urls.lensUsageAnalyticsUrl(this.props.lensDetailInfoWithUsage.id);
    }
  }

  public render() {
    const subspaces = this.memoizedGetSubspaces(
      this.props.subspaceProps,
      this.props.siteUrlName,
    );

    if (this.props.spaceInfo.subspaceName === 'usageAnalytics') {
      return <LensUsageAnalytics lensId={this.props.lensDetailInfoWithUsage.id}/>;
    }

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.spaceInfo.subspaceName}
        urlForSubspace={this.urlForSubspace}
        mastheadElement={this.mastheadElement}
        testIdPrefix={'lens'}
        spaceItemName={this.props.lensDetailInfoWithUsage.name}
      />
    );
  }

  public componentDidMount() {
    this.handleTelemetry();
    this.props.fetchUpdatedContentCounts();
  }

  public componentDidUpdate(prevProps: LensSpaceProps) {
    this.props.fetchUpdatedContentCounts();
    if (this.props.lensDetailInfoWithUsage.id !== prevProps.lensDetailInfoWithUsage.id) {
      this.handleTelemetry();
    }
  }

  private handleTelemetry() {
    // TODO: Telemetry
  }

  private get mastheadElement(): React.ReactNode {
    return <LensSpaceHeader
      availableActions={this.props.allowedActions}
      item={this.props.lensDetailInfoWithUsage}
      urls={this.props.urls}
      canEdit={this.props.canEdit}
      sanitizer={this.props.sanitizer}
      reloadSpace={this.props.reloadSpace}
    />;
  }
}

export const iframeStyle = cssClass('lens-landing-page', 'iframe', {
  border: 'none',
  width: '100%',
  height: '100%',
});

const getNlpWorkbookUrl = (siteUrlName: string, lensRepositoryUrl: string): string => {
    const baseUrl = window.location.origin;
    const siteUrl = siteUrlName === '' ? '' : `/t/${siteUrlName}`;
    return `${baseUrl}${siteUrl}/startAskData/lens/${lensRepositoryUrl}`;
};
